import { useState } from "react";
import type { FormEvent } from "react";

import { isAPIError } from "@/core/services";
import { useDialogForm } from "@/ds";
import { cadastrarDistribuicao, editarDistribuicao } from "@/modules/financeiro-configuracoes";
import type { ColetorDoTipo } from "@/modules/financeiro-configuracoes";
import { useChaveIdempotencia, useConfirmarDescarte } from "@/shared/hooks";
import { useColetoresDoTipo } from "../hooks/use-coletores-do-tipo";
import { useTiposObraOpcoes } from "../hooks/use-tipos-obra-opcoes";
import {
  COLETORES_CARREGANDO,
  DISTRIBUICAO_FORM_ID,
  FACILIDADES_NOTA,
  FACILIDADES_TITULO,
  NOME_MAX,
  NOME_NOTA_EDICAO,
  NOME_REPETIDO,
  PARTE_TITULO,
  PERCENTUAL_FIXO,
  SALVAR_INDISPONIVEL,
  SALVAR_SOMA_INVALIDA,
  SEM_TIPO_AVISO,
  TITULO_EDICAO,
  TITULO_NOVA,
  usoTexto,
} from "./distribuicao-dialog.constants";
import {
  PARTES_EDITAVEIS,
  coletoresDaParte,
  erroNome,
  erroPercentual,
  erroTipo,
  formatSoma,
  letrasDosPais,
  mensagemSoma,
  montarCorpo,
  percentualDo,
  percentuaisDa,
  faixasValidas,
  somasValidas,
  somaDaParte,
  somaValida,
  temPercentual,
} from "./distribuicao-dialog.form";
import type { DistribuicaoForm, ParteEditavel } from "./distribuicao-dialog.form";
import type {
  BlocoColetores,
  DistribuicaoDialogProps,
  DistribuicaoDialogViewModel,
  EstadoArvore,
  LinhaColetor,
} from "./types-distribuicao-dialog";

const rotuloColetor = (coletor: ColetorDoTipo) => `${coletor.codigo} · ${coletor.nome} (%)`;

export function useDistribuicaoDialog({
  modo,
  distribuicao,
  onClose,
  onConcluido,
}: DistribuicaoDialogProps): DistribuicaoDialogViewModel {
  const editando = modo === "edicao" && distribuicao !== undefined;
  const { comChave } = useChaveIdempotencia();
  const [nomeRepetido, setNomeRepetido] = useState(false);
  const [tipoPendente, setTipoPendente] = useState<string | null>(null);

  const form = useDialogForm<DistribuicaoForm>({
    initial: () =>
      editando
        ? {
            nome: distribuicao.nome,
            tipoObraId: distribuicao.tipoObra?.id ?? null,
            percentuais: percentuaisDa(distribuicao),
          }
        : { nome: "", tipoObraId: null, percentuais: {} },
    validate: ({ nome, tipoObraId }) => ({
      ...(editando ? {} : { nome: erroNome(nome) ?? undefined }),
      tipoObraId: erroTipo(tipoObraId) ?? undefined,
    }),
    onSubmit: async (valores) => {
      const corpo = montarCorpo(valores, partes);
      try {
        if (editando) {
          const edicao = { itens: corpo.itens };
          await comChave(edicao, (idempotencyKey) =>
            editarDistribuicao(distribuicao.id, edicao, { idempotencyKey }),
          );
        } else {
          await comChave(corpo, (idempotencyKey) =>
            cadastrarDistribuicao(corpo, { idempotencyKey }),
          );
        }
      } catch (erro) {
        if (isAPIError(erro) && erro.status === 409) {
          setNomeRepetido(true);
          return;
        }
        throw erro;
      }
      onConcluido();
    },
  });

  const { tipoObraId, percentuais } = form.values;
  const tipos = useTiposObraOpcoes();
  const coletores = useColetoresDoTipo(tipoObraId);
  const partes = coletores.partes;

  const descarte = useConfirmarDescarte({ onDescartar: onClose, testId: "distribuicao-descarte" });
  const trocarTipo = (id: string | null) => {
    form.setValue("tipoObraId", id);
    form.setValue("percentuais", {});
    setTipoPendente(null);
  };
  const trocaTipo = useConfirmarDescarte({
    onDescartar: () => trocarTipo(tipoPendente),
    testId: "distribuicao-troca-tipo",
  });

  const digitar = (coletorId: string, valor: number | null) =>
    form.setValue("percentuais", { ...percentuais, [coletorId]: valor });

  const linhasDe = (coletor: ColetorDoTipo, sub: boolean): LinhaColetor[] => {
    const pai = coletor.filhos.length > 0;
    const valor = pai ? percentualDo(coletor, percentuais) : (percentuais[coletor.id] ?? null);
    return [
      {
        id: coletor.id,
        rotulo: rotuloColetor(coletor),
        sub,
        valor,
        desabilitado: pai || form.isSubmitting,
        erro: pai ? null : erroPercentual(valor),
        testId: `distribuicao-percentual-${coletor.id}`,
        digitar: (novo) => digitar(coletor.id, novo),
      },
      ...coletor.filhos.flatMap((filho) => linhasDe(filho, true)),
    ];
  };

  const blocoDe = (parte: ParteEditavel): BlocoColetores => {
    const doBloco = coletoresDaParte(partes, parte);
    const soma = somaDaParte(doBloco, percentuais);
    const valida = somaValida(soma);
    const pais = letrasDosPais(doBloco);
    const dica = `Campo vazio vale 0%.${pais ? ` ${pais} ${pais.length > 1 ? "somam" : "soma"} o 2º nível.` : ""}`;
    const id = `distribuicao-bloco-${parte.toLowerCase()}`;
    return {
      id,
      titulo: PARTE_TITULO[parte],
      dica,
      somaTexto: `Soma: ${formatSoma(soma)}%`,
      somaTestId: `${id}-soma`,
      erroSomaTestId: `${id}-erro-soma`,
      somaValida: valida,
      erroSoma: valida ? null : mensagemSoma(soma),
      linhas: doBloco.flatMap((coletor) => linhasDe(coletor, false)),
    };
  };

  let estado: EstadoArvore = "pronta";
  if (!tipoObraId) estado = "sem-tipo";
  else if (coletores.erro) estado = "erro";
  else if (coletores.carregando) estado = "carregando";
  const pronta = estado === "pronta";

  const facilidades = coletoresDaParte(partes, "FACILIDADES");
  const salvarHabilitado = !tipoObraId || (pronta && somasValidas(partes, percentuais));
  const enviar = (evento: FormEvent<HTMLFormElement>) => {
    evento.preventDefault();
    if (salvarHabilitado && (!tipoObraId || faixasValidas(percentuais))) void form.submit();
  };

  const mensagensDaArvore: Record<EstadoArvore, string | null> = {
    "sem-tipo": SEM_TIPO_AVISO,
    carregando: COLETORES_CARREGANDO,
    erro: coletores.erro,
    pronta: null,
  };
  let salvarAviso: string | null = null;
  if (!salvarHabilitado) salvarAviso = pronta ? SALVAR_SOMA_INVALIDA : SALVAR_INDISPONIVEL;

  return {
    formId: DISTRIBUICAO_FORM_ID,
    titulo: editando ? TITULO_EDICAO : TITULO_NOVA,
    nome: {
      valor: form.values.nome,
      max: NOME_MAX,
      somenteLeitura: editando,
      erro: nomeRepetido ? NOME_REPETIDO : (form.errors.nome ?? null),
      nota: editando ? NOME_NOTA_EDICAO : null,
      digitar: (valor) => {
        setNomeRepetido(false);
        form.setValue("nome", valor);
      },
    },
    tipo: {
      valor: tipoObraId,
      opcoes: tipos.tipos.map((tipo) => ({
        label: `${tipo.codigo} · ${tipo.nome}`,
        value: tipo.id,
      })),
      desabilitado: editando || form.isSubmitting,
      erro: form.errors.tipoObraId ?? tipos.erro,
      carregando: tipos.carregando,
      escolher: (id) => {
        if (id === tipoObraId) return;
        if (temPercentual(percentuais)) {
          setTipoPendente(id);
          trocaTipo.pedir(true);
        } else {
          trocarTipo(id);
        }
      },
    },
    uso:
      editando && distribuicao.uso.escopos + distribuicao.uso.obras > 0
        ? usoTexto(distribuicao.uso.escopos, distribuicao.uso.obras)
        : null,
    arvore: {
      estado,
      mensagem: mensagensDaArvore[estado],
      recarregar: coletores.recarregar,
    },
    blocos: pronta ? PARTES_EDITAVEIS.map(blocoDe) : [],
    facilidades:
      pronta && facilidades.length > 0
        ? {
            titulo: FACILIDADES_TITULO,
            linhas: facilidades.map((coletor) => ({
              id: coletor.id,
              rotulo: rotuloColetor(coletor),
              valor: PERCENTUAL_FIXO,
            })),
            nota: FACILIDADES_NOTA,
          }
        : null,
    isSubmitting: form.isSubmitting,
    submitError: form.submitError,
    salvarDesabilitado: !salvarHabilitado,
    salvarAviso,
    enviar,
    cancelar: () => descarte.pedir(form.isDirty),
    descarte: descarte.confirmacao,
    trocaTipo: trocaTipo.confirmacao,
  };
}
